import { Trans, useTranslation } from "react-i18next";
import { CCard, CCardBody } from "@coreui/react-pro";

/**
 * Third-party notices.
 *
 * Apache 2.0 section 4(d) requires that where a work carries a NOTICE file, its contents are
 * reproduced somewhere customary for such notices. For a web app that is a page like this one,
 * linked from the footer — not a file in a repository a member never sees.
 */
export function Notices() {
  const { t } = useTranslation();

  return (
    <div>
      <h1 className="mb-1">{t("notices.title")}</h1>
      <p className="text-body-secondary mb-4">{t("notices.intro")}</p>

      <CCard>
        <CCardBody>
          <Notice
            name="TradingView Lightweight Charts"
            what={t("notices.entries.lightweightCharts.what")}
            licence="Apache License 2.0"
            licenceHref="https://github.com/tradingview/lightweight-charts/blob/master/LICENSE"
            last
          >
            {`TradingView Lightweight Charts™
Copyright (c) 2025 TradingView, Inc. https://www.tradingview.com/`}
          </Notice>
        </CCardBody>
      </CCard>
    </div>
  );
}

function Notice({
  name,
  what,
  licence,
  licenceHref,
  children,
  last = false,
}: {
  name: string;
  what: string;
  licence: string;
  licenceHref: string;
  children: string;
  last?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <section className={last ? "" : "mb-4"}>
      {/* The library's name and its licence's name are proper nouns, so they stay as they are. */}
      <h5 className="mb-1">{name}</h5>
      <p className="text-body-secondary mb-2">{what}</p>
      <p className="text-body-secondary mb-2">
        <Trans
          t={t}
          i18nKey="notices.licensedUnder"
          values={{ licence }}
          components={{
            licenceLink: (
              <a href={licenceHref} target="_blank" rel="noreferrer noopener" />
            ),
          }}
        />
      </p>
      {/* Verbatim, pre-formatted, and never translated. A notice rendered in another language, or
          reflowed into a paragraph, is no longer the text the licence asked us to reproduce. */}
      <pre
        className="text-body-secondary small mb-0"
        style={{ whiteSpace: "pre-wrap" }}
      >
        {children}
      </pre>
    </section>
  );
}
